import { useQuery } from "@tanstack/react-query";
import { Duration } from "luxon";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import type { UnactivatedMember } from "@storyboard/web-client";
import { getUnactivatedMembers, memberKeys } from "@storyboard/web-client";
import { transport } from "@/config";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { recordEvent } from "@/services/telemetry";

/**
 * Slow on purpose: the interval only mops up drift (new invites, account disconnections, recovery from a
 * failed refresh). The moment that matters — a driver's first login — is caught in seconds by the
 * heartbeat-triggered `refetch` in DriverView and `useDispatchRows`, so this matches the device-usage cadence rather than racing
 * it. One cohort has no fast path: a member whose alias feed the dispatcher can't read never appears in the
 * volatile query, so their first login waits out this interval.
 */
const UNACTIVATED_MEMBERS_REFRESH = Duration.fromObject({ minutes: 10 });

/** The driver role, as a stable ref for `useUnactivatedMembers` — inline `["limitedMember"]` would change every render. */
export const UNACTIVATED_DRIVER_ROLES: string[] = ["limitedMember"];

/**
 * The unactivated set narrowed to the given roles; the full set when no filter is given.
 */
export function filterUnactivatedByRole(members: readonly UnactivatedMember[], filter?: string[]): UnactivatedMember[] {
  const roleFilter = filter != null && filter.length > 0 ? filter : null;
  return roleFilter ? members.filter((member) => roleFilter.includes(member.role)) : [...members];
}

/**
 * Members of the current workspace who have never logged in (no account, or an account with no Login event),
 * optionally narrowed to the given roles.
 *
 * Polled rather than pushed — Login `AccountEvent`s are written with no client publish — with the
 * heartbeat-triggered `refetch` as the fast path: DriverView and `useDispatchRows` call it the moment a member in this set starts
 * heartbeating, which only happens over an authenticated (logged-in) session. A failed refresh keeps the last
 * known set (Query holds the previous data on error); only a failed first load leaves the column empty.
 *
 * Identity discipline matters here: `unactivatedMembershipIds` feeds the driver filter config, and a fresh
 * identity there re-runs the heavy whole-roster query (WEB-2144). The `select` sorts by membership id so a
 * reordered poll still structurally shares with the previous response; a changed field on any member (even
 * one the roster doesn't display) still produces a fresh set, which the old bespoke compare avoided — an
 * accepted loosening for deleting that code.
 */
export function useUnactivatedMembers(filter?: string[]) {
  const { currentWorkspaceId } = useContext(WorkspaceContext);

  const query = useQuery({
    queryKey: memberKeys.unactivated(currentWorkspaceId ?? ""),
    queryFn: () => getUnactivatedMembers(transport, { workspaceId: currentWorkspaceId ?? "" }),
    enabled: Boolean(currentWorkspaceId),
    refetchInterval: UNACTIVATED_MEMBERS_REFRESH.toMillis(),
    // Match the old setInterval cadence: keep ticking in background tabs, and don't let window
    // focus refire this deliberately slow roster-wide endpoint.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    select: (data) => [...(data.members ?? [])].sort((a, b) => a.membershipId.localeCompare(b.membershipId)),
  });

  // Failures are swallowed by design, so Honeycomb is the only place a persistently failing endpoint
  // shows up: a stale "Never logged in" set looks identical to a correct one. The ref seeds with the
  // cached value so a remount within gcTime doesn't re-record an already-reported failure.
  const lastRecordedErrorAt = useRef(query.errorUpdatedAt);
  useEffect(() => {
    if (query.errorUpdatedAt && query.errorUpdatedAt !== lastRecordedErrorAt.current && query.error) {
      lastRecordedErrorAt.current = query.errorUpdatedAt;
      recordEvent("unactivated_members.refresh_failed", {
        unactivatedMembers: {
          workspaceId: currentWorkspaceId,
          error: query.error instanceof Error ? query.error.message : String(query.error),
        },
      });
    }
  }, [query.errorUpdatedAt]);

  const unactivatedMembers = useMemo(() => filterUnactivatedByRole(query.data ?? [], filter), [query.data, filter]);

  const { refetch } = query;
  const refetchNow = useCallback(() => {
    void refetch();
  }, [refetch]);

  const unactivatedMembershipIds = useMemo(
    () => new Set(unactivatedMembers.map((m) => m.membershipId)),
    [unactivatedMembers],
  );

  return { unactivatedMembers, unactivatedMembershipIds, refetch: refetchNow };
}
